/** @format */
import { Choice } from '@mpb-com/feather/components/atoms/choice';
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { useKeyPress } from '@mpb-com/feather/hooks';
import React, {
    FunctionComponent,
    RefObject,
    useEffect,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import { type FilterOption } from '@toucan/client/features/search';
import { useSelectA11y } from '@toucan/client/features/search/hooks/use-select-a11y';

import { styles } from './option-list.styles';
import { SearchFilterMenuOptionListProps } from './types';

const StyledSearchFilterMenuOptionList = styled.div`
    ${styles}
`;

export const SearchFilterMenuOptionList: FunctionComponent<
    SearchFilterMenuOptionListProps
> = ({
    onSelect,
    filter,
    isLoading = false,
    displayOptionQuantity = true,
    selectedOptions = [],
    autoFocus = true,
    className = '',
    showScrollFade = true,
    'data-testid': dataTestId = 'option-list',
}) => {
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const optionsRef = useRef<HTMLUListElement>(null);

    const { getItemProps } = useSelectA11y();
    const lastActiveIndex = useRef<number>(0);

    function isScrollAtBottom(): boolean {
        const element = optionsRef?.current;

        return Boolean(
            element &&
                element.scrollHeight -
                    (element.scrollTop + element.clientHeight) <=
                    5
        );
    }

    function setScrollPosition(ref: RefObject<HTMLElement>, position: number) {
        ref.current?.scrollTo({
            top: position,
            behavior: 'smooth',
        });
    }

    const atScrollEnd = isScrollAtBottom();

    const optionHeight: number =
        (optionsRef?.current?.scrollHeight ?? 0) /
        (filter?.options?.length || 0);

    // auto focus the list of options
    useEffect(() => {
        if (!autoFocus) {
            return;
        }

        optionsRef.current?.focus();
        setActiveIndex(0);
    }, [optionsRef?.current]);

    useKeyPress(optionsRef.current, ['ArrowDown', 'Down'], (event) => {
        event.preventDefault();
        const hasFocus = optionsRef.current?.contains(document.activeElement);
        if (!filter || !hasFocus) {
            return;
        }
        const newIndex = (activeIndex + 1) % filter.options.length;
        setActiveIndex(newIndex);
        setScrollPosition(
            optionsRef,
            newIndex * optionHeight - 3 * optionHeight
        );
    });

    useKeyPress(optionsRef.current, ['ArrowUp', 'Up'], (event) => {
        event.preventDefault();
        const hasFocus = optionsRef.current?.contains(document.activeElement);
        if (!filter || !hasFocus) {
            return;
        }
        const newIndex =
            (activeIndex - 1 + filter.options.length) % filter.options.length;
        setActiveIndex(newIndex);
        setScrollPosition(
            optionsRef,
            newIndex * optionHeight - 3 * optionHeight
        );
    });

    useKeyPress(optionsRef.current, [' ', 'Enter'], (event) => {
        event.preventDefault();
        const hasFocus = optionsRef.current?.contains(document.activeElement);
        if (!filter || !hasFocus) {
            return;
        }
        handleSelect(filter.options[activeIndex], activeIndex);
    });

    function handleOptionMouseEnter(index: number) {
        lastActiveIndex.current = index;
        setActiveIndex(index);
    }

    function handleOptionsFocus() {
        setActiveIndex(lastActiveIndex.current);
    }

    function clearActiveIndex() {
        lastActiveIndex.current = activeIndex;
        setActiveIndex(-1);
    }

    function handleSelect(item: FilterOption, index: number) {
        setActiveIndex(index);
        onSelect?.(item);
    }

    function isSelected(
        option: FilterOption,
        selectedOptions: FilterOption[]
    ): boolean {
        return selectedOptions.some((selectedOption) => {
            return selectedOption.value === option.value;
        });
    }

    return (
        <StyledSearchFilterMenuOptionList
            atScrollEnd={!showScrollFade || atScrollEnd}
            className={className}
            data-testid={dataTestId}
        >
            <ul
                ref={optionsRef}
                tabIndex={0}
                onBlur={() => {
                    clearActiveIndex();
                }}
                onFocus={() => {
                    handleOptionsFocus();
                }}
            >
                {isLoading && (
                    <Skeleton variant={SkeletonVariant.TEXT} lineCount={2} />
                )}
                {!isLoading &&
                    filter.options?.map(
                        (option: FilterOption, index: number) => {
                            const label = displayOptionQuantity
                                ? `${option.label} (${option.quantity})`
                                : option.label;

                            return (
                                <li
                                    key={option.value}
                                    onMouseEnter={() => {
                                        handleOptionMouseEnter(index);
                                    }}
                                    onMouseLeave={() => {
                                        clearActiveIndex();
                                    }}
                                    data-active={
                                        !option.disabled &&
                                        index === activeIndex
                                    }
                                >
                                    <Choice
                                        tabIndex={-1}
                                        name={`${filter.label}.${index}`}
                                        label={label}
                                        value={option.value}
                                        disabled={
                                            option.quantity === 0 ||
                                            option.disabled
                                        }
                                        checked={isSelected(
                                            option,
                                            selectedOptions
                                        )}
                                        onChange={() => {
                                            handleSelect(option, index);
                                        }}
                                        {...getItemProps({
                                            key: option.value,
                                            item: option,
                                            isSelected: isSelected(
                                                option,
                                                selectedOptions
                                            ),
                                            index,
                                        })}
                                        key={option.value}
                                        className="choice"
                                    />
                                </li>
                            );
                        }
                    )}
            </ul>
        </StyledSearchFilterMenuOptionList>
    );
};
